import React, { ReactNode, useState } from "react";
import { Loader2, Lock, Minus, Package, Plus, Trash2 } from "lucide-react";
import { formatCurrency } from "@/utils/formatCurrency";
import { Cart, CartLine } from "../../types";

type Props = {
  cart: Cart;
  // CTA button (and any per-mode hint, e.g. "Confirm" for free flows). Lives
  // under the total so the user's eye lands on it after the price.
  children?: ReactNode;
  // Set to false on the confirmation screen — there's no payment to secure
  // when the transaction is already done.
  showSecurePaymentFooter?: boolean;
  // Optional per-line interactive callbacks. When BOTH are provided the
  // summary renders + / − / remove controls next to each line. When omitted
  // the cart is read-only (events, subscription).
  onUpdateLineQuantity?: (line: CartLine, quantity: number) => Promise<Cart>;
  onRemoveLine?: (line: CartLine) => Promise<Cart>;
  // Called whenever an interactive callback resolves with a new cart, so the
  // shell (parent) can sync its local cart state.
  onCartUpdate?: (cart: Cart) => void;
};

const colorBySource = {
  voucher: "text-green-600 dark:text-green-400",
  discount_code: "text-blue-600 dark:text-blue-400",
  admin_discount: "text-purple-600 dark:text-purple-400",
  other: "text-gray-700 dark:text-gray-300",
} as const;

export function CartSummary({
  cart,
  children,
  showSecurePaymentFooter = true,
  onUpdateLineQuantity,
  onRemoveLine,
  onCartUpdate,
}: Props) {
  const totalQuantity = cart.lines.reduce((n, l) => n + l.quantity, 0);
  const editable = Boolean(onUpdateLineQuantity && onRemoveLine);

  // Per-line in-flight set: keyed by sku+index pair so duplicate-sku rows are
  // distinguishable. Spinner only shows on the line currently updating.
  const [updating, setUpdating] = useState<Set<string>>(new Set());

  const lineKey = (line: CartLine, idx: number) => `${line.sku}-${idx}`;

  const runLineUpdate = async (
    line: CartLine,
    idx: number,
    fn: () => Promise<Cart>,
  ) => {
    const key = lineKey(line, idx);
    setUpdating((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    try {
      const updated = await fn();
      onCartUpdate?.(updated);
    } finally {
      setUpdating((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  return (
    <section
      className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 text-gray-900 dark:text-gray-200"
      data-testid="cart-summary"
    >
      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4">
        Order Summary
      </h3>

      {cart.lines.length > 0 ? (
        <ul className="space-y-3">
          {cart.lines.map((line, i) => {
            const key = lineKey(line, i);
            const isUpdating = updating.has(key);
            const lineTotal = line.unitPrice * line.quantity;
            return (
              <li
                className="flex items-start gap-3 text-sm"
                data-testid="cart-line"
                key={key}
              >
                {/* Thumbnail */}
                <div className="w-12 h-12 flex-shrink-0 rounded-md bg-gray-100 dark:bg-slate-700 flex items-center justify-center overflow-hidden">
                  {line.imageUrl ? (
                    <img
                      alt=""
                      className="w-full h-full object-cover"
                      src={line.imageUrl}
                    />
                  ) : (
                    <Package className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  )}
                </div>

                {/* Name + meta */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white truncate">
                    {line.name}
                  </p>
                  {line.description ? (
                    <p className="text-xs text-muted-foreground truncate">
                      {line.description}
                    </p>
                  ) : null}
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatCurrency(line.unitPrice)} each
                  </p>

                  {/* Quantity controls */}
                  {editable ? (
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="inline-flex items-center rounded-md border border-gray-200 dark:border-slate-700">
                        <button
                          aria-label="Decrease quantity"
                          className="p-1 disabled:opacity-50"
                          data-testid="cart-line-decrease"
                          disabled={isUpdating || line.quantity <= 1}
                          onClick={() =>
                            runLineUpdate(line, i, () =>
                              onUpdateLineQuantity!(line, line.quantity - 1),
                            )
                          }
                          type="button"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span
                          className="px-2 text-sm tabular-nums min-w-[1.5rem] text-center"
                          data-testid="cart-line-quantity"
                        >
                          {isUpdating ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin inline" />
                          ) : (
                            line.quantity
                          )}
                        </span>
                        <button
                          aria-label="Increase quantity"
                          className="p-1 disabled:opacity-50"
                          data-testid="cart-line-increase"
                          disabled={isUpdating}
                          onClick={() =>
                            runLineUpdate(line, i, () =>
                              onUpdateLineQuantity!(line, line.quantity + 1),
                            )
                          }
                          type="button"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <button
                        aria-label="Remove line"
                        className="p-1 text-muted-foreground hover:text-red-600 disabled:opacity-50"
                        data-testid="cart-line-remove"
                        disabled={isUpdating}
                        onClick={() =>
                          runLineUpdate(line, i, () => onRemoveLine!(line))
                        }
                        type="button"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Qty: {line.quantity}
                    </p>
                  )}
                </div>

                {/* Line total */}
                <div className="text-right text-sm font-medium tabular-nums">
                  {formatCurrency(lineTotal)}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      <div className="border-t border-gray-200 dark:border-slate-700 my-3" />

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span>
            {totalQuantity} {totalQuantity === 1 ? "item" : "items"}
          </span>
          <span data-testid="cart-subtotal">
            {formatCurrency(cart.subtotal)}
          </span>
        </div>

        {cart.discountLines.map((d, i) => (
          <div
            className={colorBySource[d.source]}
            data-testid="cart-discount-line"
            key={`${d.sku}-${i}`}
          >
            <div className="flex justify-between font-medium">
              <span>{d.name}</span>
              <span>-{formatCurrency(d.amount)}</span>
            </div>
            {d.description ? (
              <div className="text-right text-xs text-muted-foreground">
                {d.description}
              </div>
            ) : null}
          </div>
        ))}

        <div className="border-t border-gray-200 dark:border-slate-700 my-3" />

        <div className="flex justify-between text-lg font-bold dark:text-white">
          <span>Total</span>
          <span data-testid="cart-total">{formatCurrency(cart.total)}</span>
        </div>
      </div>

      {children ? <div className="pt-4">{children}</div> : null}

      {showSecurePaymentFooter && cart.total > 0 ? (
        <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mt-3">
          <Lock className="h-3 w-3" />
          <span>Secure, encrypted payment</span>
        </div>
      ) : null}
    </section>
  );
}
